import { useMutation } from '@tanstack/react-query';
import { TRequestHandlerError } from '@studio-toolkit/request-handler';

import { useQuantum } from 'hooks';
import { useTokenAndHeaders } from 'hooks/useHeaders';

import { apiUrl, requestHandler } from 'utils';

// Define your ComparePdfInput type according to your backend schema
interface ComparePdfInput {
  pickedPlatform?: string;
  orderType?: string;
  sourceVersion?: string;
  targetVersion?: string;
}

interface ComparePdfResponse {
  status: number;
  message: string;
  sourcePdfBase64?: string;
  targetPdfBase64?: string;
}

export interface IUseComparePdfReturnValues {
  comparePdf: (input: ComparePdfInput) => Promise<ComparePdfResponse>;
  compareResponse: ComparePdfResponse | undefined;
  error: unknown;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  reset: () => void;
}

const useComparePdf = (): IUseComparePdfReturnValues => {
  const { trackApiCall } = useQuantum();

  const url = apiUrl({
    endpoint: 'comparePdf',
    apiEnv: import.meta.env.VITE_APP_ENV,
  });
  const { headers } = useTokenAndHeaders();

  const {
    mutateAsync: comparePdf,
    data,
    error,
    isPending,
    isError,
    isSuccess,
    reset,
  } = useMutation<ComparePdfResponse, TRequestHandlerError, ComparePdfInput>({
    mutationKey: ['comparePdf'],
    mutationFn: async (input) => {
      const res = (await requestHandler({
        requestConfig: {
          method: 'POST',
          url: url,
          data: input,
          headers,
        },
        responseHandlers: {
          200: (res) => res,
        },
        errorHandlers: {
          500: (error) => {
            console.log(error);
            throw new Error('PDF comparison failed');
          },
        },
        quantumHandler: trackApiCall,
      })) as ComparePdfResponse;

      if (
        res.status !== 200 ||
        !res?.sourcePdfBase64 ||
        !res?.targetPdfBase64
      ) {
        throw new Error('PDF comparison failed');
      }

      return res;
    },
  });

  return {
    comparePdf,
    compareResponse: data,
    error,
    isLoading: isPending,
    isError,
    isSuccess,
    reset,
  };
};

export default useComparePdf;
